import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthError, resetPassword } from './auth';
import { AuthLink, AuthNotice, AuthShell, AuthSubmit, PasswordField } from './AuthShell';

/** Matches the platform's own minimum, so the server never rejects what the form accepted. */
const MIN_LENGTH = 8;

export default function ResetPassword({
  userId,
  token,
  onDone,
}: {
  userId: string;
  token: string;
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError(t('com_ui_leadhr_reset_too_short', { count: MIN_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t('com_ui_leadhr_reset_mismatch'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await resetPassword({ userId, token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : t('com_ui_leadhr_login_generic_error'));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell
        title={t('com_ui_leadhr_reset_done_title')}
        intro={t('com_ui_leadhr_reset_done_intro')}
        onSubmit={(event) => {
          event.preventDefault();
          onDone();
        }}
      >
        <AuthSubmit
          busy={false}
          label={t('com_ui_leadhr_reset_done_cta')}
          busyLabel={t('com_ui_leadhr_reset_done_cta')}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t('com_ui_leadhr_reset_title')}
      intro={t('com_ui_leadhr_reset_intro')}
      onSubmit={submit}
    >
      <PasswordField
        id="leadhr-new-password"
        label={t('com_ui_leadhr_reset_new')}
        value={password}
        onChange={setPassword}
        autoComplete="new-password"
        hint={t('com_ui_leadhr_reset_hint', { count: MIN_LENGTH })}
        invalid={!!error}
        describedBy={error ? 'leadhr-reset-error' : undefined}
      />
      <PasswordField
        id="leadhr-confirm-password"
        label={t('com_ui_leadhr_reset_confirm')}
        value={confirm}
        onChange={setConfirm}
        autoComplete="new-password"
        invalid={!!error}
        describedBy={error ? 'leadhr-reset-error' : undefined}
      />
      {error ? (
        <AuthNotice id="leadhr-reset-error" tone="error">
          {error}
        </AuthNotice>
      ) : null}
      <AuthSubmit
        busy={busy}
        label={t('com_ui_leadhr_reset_submit')}
        busyLabel={t('com_ui_leadhr_reset_busy')}
      />
      <AuthLink onClick={onDone}>{t('com_ui_leadhr_forgot_back')}</AuthLink>
    </AuthShell>
  );
}
